// src/components/WzmiankiDokumentySection.jsx
import React, { useMemo, useState } from "react";
import { Info, CalendarDays, FileText, List } from "lucide-react";
import { Sekcja, RowIcon, mono } from "./atoms";

/* ---------------- helpers ---------------- */
const get = (obj, path, dflt = undefined) =>
  path.split(".").reduce((o, k) => (o && o[k] !== undefined ? o[k] : dflt), obj);

const asArr = (v) => (Array.isArray(v) ? v : v ? [v] : []);

/* A tiny pill used below */
const pill = (text, { monoFont = false } = {}) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      padding: "4px 10px",
      borderRadius: 999,
      border: "1px solid #e5e7eb",
      background: "#f7faff",
      color: "#1f2937",
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: 0.2,
      marginRight: 8,
    }}
  >
    <span style={{ fontFamily: monoFont ? "ui-monospace,SFMono-Regular,Menlo,monospace" : "inherit" }}>
      {text || "—"}
    </span>
  </span>
);

/** One collapsible list block */
function MentionsList({ title, items, showDate = true, emptyText = "Brak danych" }) {
  const clean = useMemo(
    () =>
      asArr(items)
        .map((x) => ({
          date: x?.dataZlozenia || null,
          period: x?.zaOkresOdDo || null,
          note: x?.uwagi || x?.opis || null,
        }))
        .filter((x) => x.date || x.period || x.note),
    [items]
  );

  const [expanded, setExpanded] = useState(false);
  const MAX = 10;
  const visible = expanded ? clean : clean.slice(0, MAX);

  return (
    <RowIcon icon={Info} label={title}>
      {clean.length === 0 ? (
        <em style={{ color: "#6b7280" }}>{emptyText}</em>
      ) : (
        <>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {visible.map((it, i) => (
              <li key={i} style={{ padding: "6px 0", color: "#111827" }}>
                {showDate && it.date ? pill(mono(it.date), { monoFont: true }) : null}
                {it.period ? pill(it.period) : null}
                {it.note ? <span style={{ marginLeft: 6, color: "#374151" }}>{it.note}</span> : null}
              </li>
            ))}
          </ul>

          {clean.length > MAX && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              style={{
                marginTop: 8,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                border: "1px solid #dbeafe",
                background: "#eff6ff",
                color: "#1e3a8a",
                padding: "6px 10px",
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {expanded ? "Pokaż mniej" : "Pokaż więcej"}
            </button>
          )}
        </>
      )}
    </RowIcon>
  );
}

/* ---------------- component ---------------- */
export default function WzmiankiDokumentySection({
  title = "Wzmianki o złożonych dokumentach",
  rawOdpis = null,
  /** optional direct arrays if you don’t pass rawOdpis */
  finReports = null,   // wzmiankaOZlozeniuRocznegoSprawozdaniaFinansowego
  approvals = null,    // wzmiankaOZlozeniuUchwalyPostanowieniaOZatwierdzeniuRocznegoSprawozdaniaFinansowego
  activityReports = null, // wzmiankaOZlozeniuSprawozdaniaZDzialalnosci
}) {
  const d3 = rawOdpis ? get(rawOdpis, "odpis.dane.dzial3", {}) : {};

  const wzFin = finReports ?? get(
    d3,
    "wzmiankiOZlozonychDokumentach.wzmiankaOZlozeniuRocznegoSprawozdaniaFinansowego",
    []
  );

  const wzUch = approvals ?? get(
    d3,
    "wzmiankiOZlozonychDokumentach.wzmiankaOZlozeniuUchwalyPostanowieniaOZatwierdzeniuRocznegoSprawozdaniaFinansowego",
    []
  );

  const wzSpDz = activityReports ?? get(
    d3,
    "wzmiankiOZlozonychDokumentach.wzmiankaOZlozeniuSprawozdaniaZDzialalnosci",
    []
  );

  const allEmpty = asArr(wzFin).length + asArr(wzUch).length + asArr(wzSpDz).length === 0;

  return (
    <Sekcja
      title={title}
      icon={FileText}
      titleStyle={{
        fontSize: 18,
        color: "#0b74e5",
        textDecorationThickness: "2px",
        textUnderlineOffset: 4,
      }}
    >
      {allEmpty ? (
        <em style={{ color: "#6b7280" }}>Brak danych.</em>
      ) : (
        <div className="sectionGrid">
          <div className="fullRow">
            <MentionsList
              title="Roczne sprawozdania finansowe"
              items={wzFin}
              showDate
            />
          </div>

          <div className="fullRow">
            <MentionsList
              title="Uchwały dot. zatwierdzenia RSF"
              items={wzUch}
              showDate={false}  // te pozycje zwykle mają tylko okres
            />
          </div>

          <div className="fullRow">
            <MentionsList
              title="Sprawozdania z działalności"
              items={wzSpDz}
              showDate={false}
            />
          </div>
        </div>
      )}
    </Sekcja>
  );
}
